/**
 * ReservationVehicleDetail Molecule Component
 * Displays selected vehicle information in reservation flow
 */

'use client'

import { Badge } from '@/components/atoms/Badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Calendar, Car, Fuel, MapPin, Users } from 'lucide-react'
import Image from 'next/image'

interface ReservationVehicleDetailProps {
	vehicleName: string
	vehicleImage: string
	motor: string
	passengers: number
	transmission: string
	dailyRate: number
	totalRate: number
	days: number
	pickupLocation: string
	dropoffLocation: string
	pickupDate: string
	dropoffDate: string
	className?: string
}

export function ReservationVehicleDetail({
	vehicleName,
	vehicleImage,
	motor,
	passengers,
	transmission,
	dailyRate,
	totalRate,
	days,
	pickupLocation,
	dropoffLocation,
	pickupDate,
	dropoffDate,
	className,
}: ReservationVehicleDetailProps) {
	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('es-CO', {
			style: 'currency',
			currency: 'COP',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	return (
		<Card
			className={cn(
				'border-2 border-[#09194D] bg-white overflow-hidden',
				className,
			)}
		>
			{/* Title Bar */}
			<div className='bg-[#CDFF00] px-6 py-3'>
				<h3 className='font-["Bricolage_Grotesque"] text-xl font-black text-[#09194D] text-center'>
					Detalle de Reserva
				</h3>
			</div>

			<CardContent className='p-6 space-y-6'>
				{/* Vehicle Image */}
				<div className='relative w-full aspect-4/3 rounded-lg overflow-hidden bg-gray-100'>
					<Image
						src={vehicleImage}
						alt={vehicleName}
						fill
						className='object-contain'
						sizes='(max-width: 768px) 100vw, 400px'
					/>
					{/* Vehicle Specs Overlay */}
					<div className='absolute top-4 left-4 right-4 grid grid-cols-3 gap-2'>
						<Badge
							icon={Fuel}
							label={motor}
							variant='default'
							className='bg-[#09194D]/80 text-white backdrop-blur-sm'
						/>
						<Badge
							icon={Users}
							label={`${passengers} Personas`}
							variant='default'
							className='bg-[#09194D]/80 text-white backdrop-blur-sm'
						/>
						<Badge
							icon={Car}
							label={transmission}
							variant='default'
							className='bg-[#09194D]/80 text-white backdrop-blur-sm'
						/>
					</div>
				</div>

				{/* Vehicle Name */}
				<div className='bg-[#09194D] text-white text-center py-3 rounded-lg'>
					<h3 className='font-["Bricolage_Grotesque"] text-xl font-black tracking-wide'>
						{vehicleName.toUpperCase()}
					</h3>
					<p className='mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/75'>
						o similar
					</p>
				</div>

				{/* Rental Details */}
				<div className='space-y-3 text-sm'>
					<div className='flex items-start gap-2'>
						<MapPin className='w-4 h-4 text-[#09194D] mt-0.5 shrink-0' />
						<div>
							<p className='font-semibold text-[#09194D]'>Recoge</p>
							<p className='text-[#09194D]/70'>{pickupLocation}</p>
						</div>
					</div>

					<div className='flex items-start gap-2'>
						<MapPin className='w-4 h-4 text-[#09194D] mt-0.5 shrink-0' />
						<div>
							<p className='font-semibold text-[#09194D]'>Entrega</p>
							<p className='text-[#09194D]/70'>{dropoffLocation}</p>
						</div>
					</div>

					<div className='flex items-start gap-2'>
						<Calendar className='w-4 h-4 text-[#09194D] mt-0.5 shrink-0' />
						<div className='flex-1'>
							<div className='flex justify-between'>
								<span className='font-semibold text-[#09194D]'>Recoge:</span>
								<span className='text-[#09194D]/70'>{pickupDate}</span>
							</div>
							<div className='flex justify-between'>
								<span className='font-semibold text-[#09194D]'>Entrega:</span>
								<span className='text-[#09194D]/70'>{dropoffDate}</span>
							</div>
						</div>
					</div>
				</div>

				{/* Pricing */}
				<div className='pt-4 border-t-2 border-gray-200 space-y-2'>
					<div className='flex justify-between items-center'>
						<span className='text-sm font-semibold text-[#09194D]'>
							Precio por día
						</span>
						<span className='text-base font-bold text-[#09194D]'>
							{formatCurrency(dailyRate)}
						</span>
					</div>
					<div className='flex justify-between items-center'>
						<span className='text-sm font-semibold text-[#09194D]'>
							Alquiler por {days} {days === 1 ? 'día' : 'días'}
						</span>
						<span className='text-base font-bold text-[#09194D]'>
							{formatCurrency(dailyRate * days)}
						</span>
					</div>
					<div className='flex justify-between items-center pt-2 border-t border-gray-200'>
						<span className='font-["Bricolage_Grotesque"] text-base font-black text-[#09194D]'>
							Tarifa Total
						</span>
						<span className='font-["Bricolage_Grotesque"] text-2xl font-black text-[#09194D]'>
							{formatCurrency(totalRate)}
						</span>
					</div>
				</div>

				{/* Disclaimer */}
				<p className='text-xs text-center text-[#09194D]/60 italic'>
					*Kilometraje ilimitado*
				</p>
			</CardContent>
		</Card>
	)
}
